import React, { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Sheet, toast } from '@node-space/ui-components'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { useCustomerMutation } from 'hooks/mutations/Customers/useCustomerMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/beneficiaries'
import { AddCustomerPayload, BusinessCustomerType, IndividualCustomerType } from 'types/customers'
import { ModalStep } from '../../types'
import { getCustomerFields } from '../../utils'
import { AddCustomerStep } from './AddCustomerStep'

interface AddCustomerProps {
  handleCloseSheet: () => void
  handleOnSuccessCloseSheet: () => void
  isSheetOpen: boolean
}

export const AddCustomer = ({
  isSheetOpen,
  handleCloseSheet,
  handleOnSuccessCloseSheet,
}: AddCustomerProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [modalStep, setModalStep] = useState<ModalStep>(ModalStep.ADD_CUSTOMER)
  const [customerFormState, setCustomerFormState] = useState<AddCustomerPayload>(
    {} as AddCustomerPayload
  )
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: submitCustomer, isPending: isLoading } = useCustomerMutation()

  const modalHeader = useMemo(() => {
    if (modalStep === ModalStep.CONFIRMATION) {
      return t('customers.confirmDetails')
    }

    return t('customers.newCustomer')
  }, [modalStep])

  const submit = () => {
    const customer = getCustomerFields(customerFormState)

    submitCustomer(customer, {
      onSuccess: () => {
        handleOnSuccessCloseSheet()
        toast({ header: t('customers.addCustomer.success'), variant: 'success' })
        queryClient.invalidateQueries({ queryKey: ['customers'] })
      },
      onError: (error: BaseErrorResponse) => {
        toast({ header: t('customers.addCustomer.error'), variant: 'error' })

        const errorDetails = error?.data?.details?.errors ?? null
        const sentryTraceId = errorDetails
          ? null
          : logSentryErrorAndGetTraceId('Error: could not add customer', error)

        setRequestError({
          show: true,
          errorCode: error.status,
          sentryTraceId,
          ErrorMessages: errorDetails,
        })
      },
    })
  }

  const appendCustomerDetails = async (customerDetails: AddCustomerPayload) => {
    setCustomerFormState({ ...customerFormState, ...customerDetails })
    setModalStep(ModalStep.CONFIRMATION)
  }

  const onBackHandler = () => {
    switch (modalStep) {
      case ModalStep.ADD_CUSTOMER:
        handleCloseSheet()
        break
      case ModalStep.CONFIRMATION:
        requestError && resetRequestError()
        setModalStep(ModalStep.ADD_CUSTOMER)
        break
      default:
        break
    }
  }

  const resetForm = () => {
    setCustomerFormState({} as AddCustomerPayload)
    setModalStep(ModalStep.ADD_CUSTOMER)
    requestError && resetRequestError()
  }

  const handleOnOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm()
    }
  }

  return (
    <Sheet
      isOpen={isSheetOpen}
      header={modalHeader}
      onBack={onBackHandler}
      onClose={handleCloseSheet}
      onOpenChange={handleOnOpenChange}
    >
      <AddCustomerStep
        modalStep={modalStep}
        customerFormState={customerFormState}
        onBackHandler={onBackHandler}
        submit={submit}
        appendCustomerDetails={appendCustomerDetails}
        isLoading={isLoading}
        requestError={requestError}
        resetRequestError={resetRequestError}
      />
    </Sheet>
  )
}
